/**
 * Vouch ledger picker (MOR-574) — choose the GL / Xero library document(s) to
 * vouch bank transactions against.
 *
 * Reuses the same Finder (`FileSystem` + `buildLibraryItems`) as the Documents
 * Library so the picker looks and behaves identically to the Files view —
 * folder tree, list columns (Category / Status / Uploaded by), built-in search.
 * Read-only (no upload / move / rename); the only interaction is selecting
 * file(s) and confirming. The chosen document ids are saved to the task; the
 * panel fetches + parses their spreadsheet rows.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { Loader2, Upload } from "lucide-react";
import {
  FileSystem,
  type FileSystemItem,
} from "@/ui/composed/file-system/file-system";
import { useLibraryUpload } from "../../library/useLibraryUpload";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/ui/composed/overlays/dialog";
import { Button } from "@/ui/primitives/button";
import { CATEGORY_LABELS } from "../../constants";
import { ProcessingStatusBadge } from "../../documents/ProcessingStatusBadge";
import {
  buildLibraryItems,
  type LibraryItemMeta,
} from "../../library/manifest";
import type { LedgerDocResult } from "./useVouchLedger";

export function VouchLedgerPicker({
  open,
  onClose,
  jobId,
  taskId,
  selectedIds,
  perDoc,
}: {
  open: boolean;
  onClose: () => void;
  jobId: Id<"jobs">;
  taskId: Id<"summarizationTasks">;
  selectedIds: string[];
  perDoc: LedgerDocResult[];
}) {
  const { data: folders } = useQuery(
    convexQuery(api.jobs.gatherInformation.folders.queries.listFolders, {
      jobId,
    }),
  );
  const { data: documents } = useQuery(
    convexQuery(api.jobs.gatherInformation.documents.queries.listDocuments, {
      jobId,
    }),
  );

  const { items, metaByPath } = useMemo(
    () => buildLibraryItems(folders ?? [], documents ?? []),
    [folders, documents],
  );

  const perDocById = useMemo(() => {
    const m = new Map<string, LedgerDocResult>();
    for (const r of perDoc) m.set(r.documentId, r);
    return m;
  }, [perDoc]);

  // Document ids selected in the Finder (the confirm target).
  const [selectedDocIds, setSelectedDocIds] = useState<string[]>(selectedIds);
  useEffect(() => {
    if (open) setSelectedDocIds(selectedIds);
  }, [open, selectedIds]);

  const handleSelectionItemsChange = (fsItems: FileSystemItem[]) => {
    const ids: string[] = [];
    for (const item of fsItems) {
      if (item.kind !== "file") continue;
      const meta = metaByPath.get(item.path) as LibraryItemMeta | undefined;
      if (meta?.documentId) ids.push(meta.documentId);
    }
    setSelectedDocIds(ids);
  };

  const { mutateAsync: setLedgerDocs, isPending } = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.bankTransactions.publicMutations
        .setVouchLedgerDocuments,
    ),
  });

  // Upload a ledger file directly from the picker (into the library root) in
  // case the GL export isn't in the library yet. The new document streams into
  // the Finder via the reactive listDocuments query.
  const { handlePickedFiles, uploadingCount } = useLibraryUpload(jobId);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSave = async () => {
    await setLedgerDocs({
      taskId,
      documentIds: selectedDocIds as Id<"documents">[],
    });
    onClose();
  };

  const itemColumnsHeader = (
    <>
      <span className="w-44 shrink-0">Category</span>
      <span className="w-40 shrink-0">Status</span>
      <span className="w-28 shrink-0">Ledger rows</span>
    </>
  );

  const renderItemColumns = (item: FileSystemItem) => {
    const path =
      item.kind === "folder" && !item.path.endsWith("/")
        ? `${item.path}/`
        : item.path;
    const meta = metaByPath.get(path) as LibraryItemMeta | undefined;
    const isFile = item.kind === "file";
    const result = meta?.documentId
      ? perDocById.get(meta.documentId)
      : undefined;

    return (
      <>
        <span className="w-44 shrink-0 truncate text-xs text-muted-foreground">
          {isFile
            ? (CATEGORY_LABELS[meta?.category ?? ""] ?? meta?.category)
            : null}
        </span>
        <span className="flex w-40 shrink-0 items-center">
          {isFile && (
            <ProcessingStatusBadge
              extractionStatus={meta?.extractionStatus}
              classificationStatus={meta?.classificationStatus}
              indexingStatus={meta?.indexingStatus}
            />
          )}
        </span>
        <span className="w-28 shrink-0 text-xs text-muted-foreground">
          {result?.status === "ok"
            ? `${result.rowCount.toLocaleString()} rows`
            : result?.status === "empty"
              ? "0 rows"
              : result?.status === "unsupported"
                ? "not a spreadsheet"
                : result?.status === "error"
                  ? "parse error"
                  : null}
        </span>
      </>
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle>Vouch against the ledger</DialogTitle>
          <DialogDescription>
            Select the accounting ledger / Xero / GL export from the library.
            Its rows are matched against the bank transactions on date + amount.
            CSV / XLSX exports only.
          </DialogDescription>
        </DialogHeader>

        <div className="flex h-[460px] min-h-0 flex-col">
          <FileSystem
            items={items}
            defaultView="list"
            className="min-h-0 flex-1"
            readOnly
            itemColumnsHeader={itemColumnsHeader}
            renderItemColumns={renderItemColumns}
            onSelectionItemsChange={handleSelectionItemsChange}
          />
        </div>

        <DialogFooter className="items-center">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".csv,.xlsx,.xls,.xlsm"
            className="hidden"
            onChange={(e) => {
              const files = Array.from(e.target.files ?? []);
              if (files.length > 0) void handlePickedFiles(files);
              e.target.value = "";
            }}
          />
          <Button
            variant="outline"
            size="sm"
            className="mr-auto gap-1.5"
            onClick={() => fileInputRef.current?.click()}
            disabled={isPending}
          >
            {uploadingCount > 0 ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Upload className="h-3.5 w-3.5" />
            )}
            {uploadingCount > 0
              ? `Uploading ${uploadingCount}…`
              : "Upload ledger file"}
          </Button>
          <span className="text-xs text-muted-foreground">
            {selectedDocIds.length} selected
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button size="sm" onClick={handleSave} disabled={isPending}>
            {isPending ? "Saving…" : "Save & vouch"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
